import React from 'react';
import {
	Button,
	Col,
	Input,
	Row,
	Label,
	ModalBody,
	Form,
	FormFeedback,
	ModalFooter,
	Spinner,
} from 'reactstrap';
import { Select, MultiSelect } from '@mantine/core';
import DraftStatusBar from '../../../../Components/Common/DraftStatusBar';
import useIncumplimientoForm from './useIncumplimientoForm';
import RiskInfoPopover from './RiskInfoPopover';
import DeadlineDateField from './DeadlineDateField';
import IncumplimientoDraftModals from './IncumplimientoDraftModals';
import { sanitizeStrArr } from '../../Fiscalizaciones/utils';

const IncumplimientoForm = ({
	dataIncumplimiento,
	dataFiscalizacion,
	handleToggleModal,
	initialFormValues,
	onFormValuesChange,
	onRequestCreateResponsible,
	isEdit,
	handleSave,
	tipoFiscalizacion,
	isEnRevision,
	inspectionTypeOptions,
	inspectionAuthorityOptions,
	responsibleOptions,
	breachResponsibleAreaOptions,
	breachImpactOptions,
	breachOriginOptions,
	breachTypeOfEvidenceOptions,
	breachCategoryOptions,
	breachRiskClassificationOptions,
}) => {
	const {
		isAdding,
		isUpdating,
		submittingRef,
		validation,
		inspectionTypeData,
		responsibleData,
		responsibleAreaData,
		originData,
		evidenceData,
		categoryData,
		riskData,
		inspectionAuthorityData,
		selectedClosureFile,
		setSelectedClosureFile,
		hasDraft,
		lastSaved,
		resumeDraftModalOpen,
		discardDraftModalOpen,
		setDiscardDraftModalOpen,
		handleResumeDraft,
		handleStartFresh,
		handleDiscardDraft,
		handleConfirmDiscard,
	} = useIncumplimientoForm({
		dataIncumplimiento,
		dataFiscalizacion,
		initialFormValues,
		isEdit,
		handleSave,
		tipoFiscalizacion,
		isEnRevision,
		inspectionTypeOptions,
		inspectionAuthorityOptions,
		responsibleOptions,
		breachResponsibleAreaOptions,
		breachImpactOptions,
		breachOriginOptions,
		breachTypeOfEvidenceOptions,
		breachCategoryOptions,
		breachRiskClassificationOptions,
	});

	const { values, errors, submitCount, handleChange, handleBlur, setFieldValue, handleSubmit, resetForm } = validation;

	React.useEffect(() => {
		if (typeof onFormValuesChange === 'function') {
			onFormValuesChange(values);
		}
	}, [onFormValuesChange, values]);

	return (
		<Form
			className='tablelist-form'
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
				return false;
			}}
		>
			<ModalBody className='modal-body'>
				{!isEdit && (
					<DraftStatusBar
						hasDraft={hasDraft}
						lastSaved={lastSaved}
						onDiscard={handleDiscardDraft}
					/>
				)}
				<Row className='g-3'>
					{/* Incumplimiento */}
					<Col lg={12} className='mb-3'>
						<Label for='description' className='form-label'>
							Incumplimiento <span className='text-danger'>*</span>
						</Label>
						<Input
							name='description'
							id='description'
							className='form-control'
							type='textarea'
							rows='3'
							placeholder='Describa el incumplimiento detectado...'
							value={values.description || ''}
							onChange={handleChange}
							onBlur={handleBlur}
							invalid={submitCount > 0 && !!errors.description}
						/>
						{submitCount > 0 && errors.description && (
							<FormFeedback>{errors.description}</FormFeedback>
						)}
					</Col>

					{/* Medida Correctiva */}
					<Col lg={12} className='mb-3'>
						<Label for='corrective_measure' className='form-label'>
							Medida Correctiva <span className='text-danger'>*</span>
						</Label>
						<Input
							name='corrective_measure'
							id='corrective_measure'
							className='form-control'
							type='textarea'
							rows='3'
							placeholder='Describa la medida correctiva propuesta...'
							value={values.corrective_measure || ''}
							onChange={handleChange}
							onBlur={handleBlur}
							invalid={submitCount > 0 && !!errors.corrective_measure}
						/>
						{submitCount > 0 && errors.corrective_measure && (
							<FormFeedback>{errors.corrective_measure}</FormFeedback>
						)}
					</Col>

					{/* Tipo de Fiscalización */}
					<Col lg={4} className='mb-3'>
						<Label className='form-label'>
							Tipo de Fiscalización <span className='text-danger'>*</span>
						</Label>
						<Select
							data={inspectionTypeData}
							value={values.inspection_type_id || null}
							onChange={value => setFieldValue('inspection_type_id', value || '')}
							placeholder='Seleccione tipo'
							disabled
							error={submitCount > 0 && errors.inspection_type_id ? errors.inspection_type_id : null}
						/>
					</Col>

					{/* Entidad Fiscalizadora */}
					<Col lg={4} className='mb-3'>
						<Label className='form-label'>Entidad Fiscalizadora</Label>
						<Select
							data={inspectionAuthorityData}
							value={values.inspection_authority_id || null}
							onChange={value => setFieldValue('inspection_authority_id', value || '')}
							placeholder='Seleccione entidad'
							searchable
							clearable
							error={submitCount > 0 && errors.inspection_authority_id ? errors.inspection_authority_id : null}
						/>
					</Col>

					{/* Área Responsable */}
					<Col lg={4} className='mb-3'>
						<Label className='form-label'>
							Área Responsable <span className='text-danger'>*</span>
						</Label>
						<Select
							data={responsibleAreaData}
							value={values.breach_responsible_area_id || null}
							onChange={value => setFieldValue('breach_responsible_area_id', value || '')}
							placeholder='Seleccione área'
							searchable
							clearable
							error={submitCount > 0 && errors.breach_responsible_area_id ? errors.breach_responsible_area_id : null}
						/>
					</Col>

					{/* Responsable(s) */}
					<Col lg={4} className='mb-3'>
						<Label className='form-label'>
							Responsable(s) <span className='text-danger'>*</span>
						</Label>
						<MultiSelect
							data={responsibleData}
							value={values.responsible_user_ids || []}
							onChange={vals => setFieldValue('responsible_user_ids', vals)}
							placeholder='Seleccione responsable(s)'
							searchable
							creatable
							getCreateLabel={query => `+ Create ${query}`}
							onCreate={query => {
								const trimmedQuery = (query || '').trim();
								if (!trimmedQuery) return query;
								if (typeof onRequestCreateResponsible === 'function') {
									onRequestCreateResponsible(trimmedQuery, values);
								}
								return trimmedQuery;
							}}
							clearable
							error={submitCount > 0 && errors.responsible_user_ids ? errors.responsible_user_ids : null}
						/>
					</Col>

					{/* Origen del Incumplimiento (solo en revisión) */}
					{isEnRevision && (
						<Col lg={4} className='mb-3'>
							<Label className='form-label'>Origen del Incumplimiento</Label>
							<Select
								data={originData}
								value={values.breach_origin_id || null}
								onChange={value => setFieldValue('breach_origin_id', value || '')}
								placeholder='Seleccione origen'
								searchable
								clearable
								error={submitCount > 0 && errors.breach_origin_id ? errors.breach_origin_id : null}
							/>
						</Col>
					)}

					{/* Fecha Límite */}
					<Col lg={4} className='mb-3'>
						<DeadlineDateField
							value={values.breach_date}
							noDeadline={values.no_deadline}
							onChange={value => setFieldValue('breach_date', value)}
							onNoDeadlineChange={checked => {
								setFieldValue('no_deadline', checked);
								if (checked) setFieldValue('breach_date', null);
							}}
							error={submitCount > 0 && errors.breach_date ? errors.breach_date : null}
						/>
					</Col>

					{/* Tipo de Evidencia */}
					<Col lg={4} className='mb-3'>
						<Label className='form-label'>
							Tipo de Evidencia <span className='text-danger'>*</span>
						</Label>
					<MultiSelect
						data={evidenceData}
						value={sanitizeStrArr(values.breach_type_of_evidence_id)}
						onChange={value => setFieldValue('breach_type_of_evidence_id', sanitizeStrArr(value))}
							placeholder='Seleccione tipo de evidencia'
							searchable
							clearable
							error={submitCount > 0 && errors.breach_type_of_evidence_id ? errors.breach_type_of_evidence_id : null}
						/>
					</Col>

					{/* Categoría del Incumplimiento */}
					<Col lg={isEnRevision ? 4 : 6} className='mb-3'>
						<Label className='form-label'>
							Categoría del Incumplimiento <span className='text-danger'>*</span>
						</Label>
						<Select
							data={categoryData}
							value={values.breach_category_id || null}
							onChange={value => setFieldValue('breach_category_id', value || '')}
							placeholder='Seleccione categoría'
							searchable
							clearable
							error={submitCount > 0 && errors.breach_category_id ? errors.breach_category_id : null}
						/>
					</Col>

					{/* Clasificación de Riesgo */}
					<Col lg={isEnRevision ? 4 : 6} className='mb-3'>
						<RiskInfoPopover
							data={riskData}
							value={values.breach_risk_classification_id}
							onChange={value => setFieldValue('breach_risk_classification_id', value)}
							error={submitCount > 0 && errors.breach_risk_classification_id ? errors.breach_risk_classification_id : null}
						/>
					</Col>
				</Row>
			</ModalBody>

			<ModalFooter>
				<div className='hstack gap-2 justify-content-end'>
					<Button
						type='button'
						onClick={() => {
							resetForm();
							handleToggleModal();
						}}
						className='btn-danger btn-label'
					>
						<i className='ri-close-line label-icon'></i>
						Cancelar
					</Button>
					<button
						type='submit'
						className='btn btn-label btn-primary'
						disabled={isAdding || isUpdating}
						onClick={e => {
							if (submittingRef.current || isAdding || isUpdating) {
								e.preventDefault();
								return;
							}
							submittingRef.current = true;
						}}
					>
						{isAdding || isUpdating ? (
							<i className='label-icon'><Spinner size='sm' /></i>
						) : (
							<i className='ri-save-line label-icon'></i>
						)}
						Guardar Incumplimiento
					</button>
				</div>
			</ModalFooter>

			<IncumplimientoDraftModals
				resumeOpen={resumeDraftModalOpen}
				discardOpen={discardDraftModalOpen}
				onResume={handleResumeDraft}
				onStartFresh={handleStartFresh}
				onDiscardConfirm={handleConfirmDiscard}
				onDiscardCancel={() => setDiscardDraftModalOpen(false)}
			/>
		</Form>
	);
};

export default IncumplimientoForm;

